import React, { useCallback, Suspense } from 'react';
import { MetaContextProvider, KeyboardWrapper, ProductTabs, useSessionUserInput } from '../..';
import { useTypedAggregatorClient } from '../../aggregator-client-context';
import { ActiveProductSearchInput } from '../../active-product-search-input';
import { initialisationExperience } from '../../../common/ufo-experiences';
import { useQuery } from '../../query-context';
import { useUserContext } from '../../../common/user-context';
import { useAppliedFilters } from '../../filters';
import { useSearchSessionId } from '../../../common/search-session-provider';
import { useDialogExpansionContext } from '../../dialog-expansion-context';
import { useUFOComponentExperience } from '@atlassian/ufo';
import { ActiveProductProvider } from '../../product-router';
import { DisableXPSAB } from '../../ab-test-context/experiment-hoc';
import { ProductSearchDialogWrapper } from './product-search-dialog-wrapper';
var ConditionalWrapper = function ConditionalWrapper(_ref) {
  var _wrapper;
  var wrapper = _ref.wrapper,
    children = _ref.children;
  return (_wrapper = wrapper === null || wrapper === void 0 ? void 0 : wrapper({
    children: children
  })) !== null && _wrapper !== void 0 ? _wrapper : children;
};
var SearchAIDialogComponent = /*#__PURE__*/React.lazy(function () {
  return import( /* webpackChunkName: "@atlaskit-internal_@atlassian/search-ai" */'@atlassian/search-ai').then(function (_ref2) {
    var SearchAIDialog = _ref2.SearchAIDialog;
    return {
      default: SearchAIDialog
    };
  });
});
var SearchAIAnswerDialog = function SearchAIAnswerDialog(_ref3) {
  var aiSearchConfig = _ref3.aiSearchConfig,
    onClose = _ref3.onClose,
    edition = _ref3.edition,
    closeParentDialog = _ref3.closeParentDialog;
  var _useQuery = useQuery(),
    query = _useQuery.query,
    setQueryOnly = _useQuery.setQueryOnly;
  var aggregatorClient = useTypedAggregatorClient();
  var cloudId = aggregatorClient.getAggregatorConfig().cloudId;
  var searchSessionId = useSearchSessionId();
  var _useUserContext = useUserContext(),
    user = _useUserContext.user;
  var _useSessionUserInput = useSessionUserInput(),
    resetSearchSession = _useSessionUserInput.resetSearchSession;
  var _useDialogExpansionCo = useDialogExpansionContext(),
    allowChangeExpand = _useDialogExpansionCo.allowChangeExpand;
  var filters = useAppliedFilters('confluence');
  var onNavigate = useCallback(function () {
    resetSearchSession();
    allowChangeExpand(true);
  }, [resetSearchSession, allowChangeExpand]);
  return /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(React.Fragment, null)
  }, /*#__PURE__*/React.createElement(SearchAIDialogComponent, {
    query: query,
    setQuery: setQueryOnly,
    cloudId: cloudId,
    getAIAnswer: aiSearchConfig.aiSearchQueryFunction,
    edition: edition,
    followUpsEnabled: aiSearchConfig.followUpsEnabled,
    onNavigate: onNavigate,
    onClose: onClose,
    searchSessionId: searchSessionId,
    userDetails: user,
    source: "searchDialog",
    parsedFilters: filters,
    assistanceServiceEnabled: aiSearchConfig.assistanceServiceEnabled,
    closeParentDialog: closeParentDialog
  }));
};
export var MultiProductDialog = function MultiProductDialog(_ref4) {
  var children = _ref4.children,
    theme = _ref4.theme,
    isExpanded = _ref4.isExpanded,
    setIsExpanded = _ref4.setIsExpanded,
    abTestCloudId = _ref4.abTestCloudId,
    aggregatorUrl = _ref4.aggregatorUrl,
    dialogFeatures = _ref4.dialogFeatures,
    user = _ref4.user,
    forwardRef = _ref4.forwardRef,
    onNavigateGeneric = _ref4.onNavigateGeneric,
    keepExpandedInTab = _ref4.keepExpandedInTab,
    pageLoadMetrics = _ref4.pageLoadMetrics,
    DialogWrapper = _ref4.DialogWrapper,
    OnboardingRenderer = _ref4.OnboardingRenderer,
    experience = _ref4.experience,
    aiSearchConfig = _ref4.aiSearchConfig,
    edition = _ref4.edition,
    primaryProduct = _ref4.primaryProduct;
  useUFOComponentExperience(initialisationExperience);
  return /*#__PURE__*/React.createElement(MetaContextProvider, {
    theme: theme,
    experience: experience,
    isExpanded: isExpanded,
    setIsExpanded: setIsExpanded,
    abTestCloudId: abTestCloudId,
    aggregatorUrl: aggregatorUrl,
    clientProviderFeatures: dialogFeatures,
    user: user,
    dialogFeatures: dialogFeatures,
    primaryProduct: primaryProduct
  }, /*#__PURE__*/React.createElement(KeyboardWrapper, {
    forwardRef: forwardRef,
    keepExpandedInTab: keepExpandedInTab
  }, function (_ref5) {
    var ref = _ref5.ref,
      onRetry = _ref5.onRetry;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ActiveProductSearchInput, {
      forwardRef: ref,
      onNavigateGeneric: onNavigateGeneric,
      features: dialogFeatures,
      pageLoadMetrics: pageLoadMetrics
    }), /*#__PURE__*/React.createElement(ConditionalWrapper, {
      wrapper: DialogWrapper
    }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ActiveProductProvider, null, function (_ref6) {
      var activeProduct = _ref6.activeProduct,
        showSearchAIDialog = _ref6.showSearchAIDialog,
        onCloseAIDialog = _ref6.onCloseAIDialog;
      return showSearchAIDialog && isExpanded && aiSearchConfig ? /*#__PURE__*/React.createElement(SearchAIAnswerDialog, {
        aiSearchConfig: aiSearchConfig,
        onClose: onCloseAIDialog,
        edition: edition,
        closeParentDialog: function closeParentDialog() {
          return setIsExpanded(false);
        }
      }) : /*#__PURE__*/React.createElement(ProductSearchDialogWrapper, null, OnboardingRenderer && isExpanded && /*#__PURE__*/React.createElement(DisableXPSAB, null, /*#__PURE__*/React.createElement(OnboardingRenderer, {
        activeProductId: activeProduct === null || activeProduct === void 0 ? void 0 : activeProduct.id
      })), /*#__PURE__*/React.createElement(ProductTabs, null), children({
        ref: ref,
        onRetry: onRetry
      }));
    }))));
  }));
};